import { useEffect, useId, useState, type FormEvent } from "react";
import { useUpdatePlacement } from "../../api/queries";
import type { Node, Placement } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import { EnvOverridesEditor, recordToRows, rowsToRecord, type EnvOverrideRow } from "./EnvOverridesEditor";
import "../../components/Form.css";
import "./services.css";

interface EditPlacementModalProps {
  /** The placement being edited, or null when the dialog is closed — same
   * "driven by the target" convention as `RotateSecretModal`. */
  placement: Placement | null;
  node: Node | undefined;
  projectId: string;
  serviceId: string;
  onClose: () => void;
}

/**
 * Edits a placement's env overrides and co-locate tag only — never the node
 * or status (`UpdatePlacementInput` doesn't carry either; ADR-0025 §2: a
 * node move is delete-then-create, and status is server-owned, S-15).
 */
export function EditPlacementModal({ placement, node, projectId, serviceId, onClose }: EditPlacementModalProps) {
  const coLocateId = useId();
  const { pushToast } = useToast();
  const mutation = useUpdatePlacement(projectId, serviceId);

  const [coLocate, setCoLocate] = useState("");
  const [rows, setRows] = useState<EnvOverrideRow[]>([]);

  // Re-seed whenever a *different* placement opens — never clobbers an
  // in-progress edit on a background refetch of the same one.
  useEffect(() => {
    if (!placement) return;
    setCoLocate(placement.coLocate);
    setRows(recordToRows(placement.envOverrides));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement?.id]);

  function handleClose() {
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!placement) return;
    mutation.mutate(
      {
        placementId: placement.id,
        input: { envOverrides: rowsToRecord(rows), coLocate: coLocate.trim() },
      },
      {
        onSuccess: () => {
          pushToast({ title: "Updated.", tone: "success" });
          handleClose();
        },
      },
    );
  }

  return (
    <Modal
      open={placement !== null}
      title={node ? `Edit config on ${node.name}` : "Edit config"}
      onClose={handleClose}
      initialFocusId={coLocateId}
    >
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't save these changes."}
          </p>
        )}

        <label className="form-field" htmlFor={coLocateId}>
          <span>Co-locate tag</span>
          <input
            id={coLocateId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={coLocate}
            onChange={(e) => setCoLocate(e.target.value)}
          />
          <span className="form-field__hint">Leave blank to clear it.</span>
        </label>

        <EnvOverridesEditor rows={rows} onChange={setRows} disabled={mutation.isPending} />

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
